import { yupResolver } from '@hookform/resolvers/yup'
import type { ICountry, IState } from 'country-state-city'
import { Country, State } from 'country-state-city'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import type { InferType } from 'yup'
import { object, string } from 'yup'

import { useLazyGetAllTeamsQuery } from '../../services/tenant/teamManagement'
import { useLazyGetAllUsersQuery } from '../../services/tenant/users'
import {
    useCreateWorkGroupsMutation,
    useUpdateWorkGroupsMutation,
} from '../../services/tenant/workGroups'
import type { ITeamMember, TeamStatusType } from '../../types/tenant/team.types'
import type {
    IgroupTeams,
    IWorkGroup,
} from '../../types/tenant/workGroup.types'
import { forwardGeocode } from '../../utils/helpers'
import { optionsTeamStatus } from '../../utils/selectsOptionsConst'
import Button from '../common/Button'
import Input, { InputStyles } from '../common/Input'
import Modal from '../common/Modal'
import SearchInputWidthDrop from '../common/SearchInputWidthDrop'
import SelectBox from '../common/SelectBox'
import SelectBoxAsynk from '../common/SelectBoxAsynk'
import SelectedUsersList from '../common/SelectedUsersList'
import type { UserPartListProps } from '../common/SelectedUsersList'
import { UserRoleEnum } from '@/types'
import { useTeamsOptions } from '@/hooks/selectOptions/useTeamsOptions'

const schema = object({
    name: string().required('WorkGroup name is required'),
    status: string().required('Status is required'),
    country: string(),
    state: string(),
    city: string(),
    street: string(),
    postalCode: string(),
    latitude: string(),
    longitude: string(),
})

type FormType = InferType<typeof schema>

interface AddWorkGroupModalProps {
    isOpen: boolean
    onClose: () => void
    selectedWorkGroup?: IWorkGroup | undefined | null
    setSelectedWorkGroup?: React.Dispatch<
        React.SetStateAction<IWorkGroup | null>
    >
}

const AddWorkGroupModal = ({
    isOpen,
    onClose,
    selectedWorkGroup,
    setSelectedWorkGroup,
}: AddWorkGroupModalProps) => {
    const [getUsers] = useLazyGetAllUsersQuery()
    const [createGroup] = useCreateWorkGroupsMutation()
    const [updateGroup] = useUpdateWorkGroupsMutation()
    const [states, setStates] = useState<{ value: string; label: string }[]>([])
    const { loadOptionsTeams } = useTeamsOptions()
    const [selectedTeams, setSelectedTeams] = useState<IgroupTeams[] | []>([])
    const {
        register,
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<FormType>({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            status: 'active',
        },
    })

    const [selectedUsers, setSelectedUsers] = useState<UserPartListProps[]>([])
    const handelClose = () => {
        onClose()
        if (setSelectedWorkGroup) setSelectedWorkGroup(null)
    }
    const country = watch('country')

    const allCountries = Country.getAllCountries()
    const countries =
        allCountries?.map((item: ICountry) => ({
            value: item.isoCode,
            label: item.name,
        })) || []

    const getStates = (country: string) => {
        if (country) {
            const allStates = State.getStatesOfCountry(country)
            setStates(
                allStates?.map((item: IState) => ({
                    value: item.isoCode,
                    label: item.name,
                })) || [],
            )
        } else {
            setStates([])
        }
    }

    useEffect(() => {
        const countryTemp =
            countries?.find(
                one => one?.label === country || one?.value === country,
            )?.value || ''
        getStates(countryTemp)
    }, [country])

    useEffect(() => {
        if (selectedWorkGroup) {
            setValue('name', selectedWorkGroup.name)
            setValue('status', selectedWorkGroup.status as TeamStatusType)
            setValue('country', selectedWorkGroup?.address?.country || '')
            setValue('state', selectedWorkGroup?.address?.state || '')
            setValue('city', selectedWorkGroup?.address?.city || '')
            setValue('street', selectedWorkGroup?.address?.street || '')
            setValue('postalCode', selectedWorkGroup?.address?.postalCode || '')
            setValue('latitude', selectedWorkGroup?.address?.latitude || '')
            setValue('longitude', selectedWorkGroup?.address?.longitude || '')
            setSelectedTeams(selectedWorkGroup.groupTeams || [])
            setSelectedUsers(
                selectedWorkGroup.groupMembers.map(member => ({
                    id: member.id,
                    name: member.fullName,
                    email: member.email,
                    role: member.roles[0],
                    src: member.photo,
                })) || [],
            )
        }
    }, [selectedWorkGroup])

    const removeSelectedUser = (id: number) => {
        setSelectedUsers(selectedUsers.filter(user => user.id !== id))
    }

    const selectUserSearch = (user: ITeamMember) => {
        console.log(user, 'selectUserSearch')

        if (!selectedUsers.find(u => u.id === user.id)) {
            setSelectedUsers(prev => [
                ...prev,
                {
                    id: user.id,
                    name: user.fullName,
                    email: user.email,
                    src: user.photo,
                    role: user.roles[0],
                },
            ])
        }
        // setSelectedUsers()
    }

    const onSubmit = async (data: FormType) => {
        const fullAddress = `${data?.street}, ${data?.city}, ${data?.state}`

        const coordinates = await forwardGeocode(fullAddress)

        const formData = {
            ...data,
            status: data.status as TeamStatusType,
            members: selectedUsers.length
                ? selectedUsers.map(user => user.id)
                : [],
            teams: selectedTeams.length
                ? selectedTeams.map(team => team.id)
                : [],
            address: {
                state: data.state,
                city: data.city,
                street: data.street,
                postalCode: data.postalCode,
                country: data.country,
                latitude: coordinates?.lat || 0,
                longitude: coordinates?.lng || 0,
            },
        }

        console.log('formData', formData)

        let res = null
        try {
            if (selectedWorkGroup) {
                res = await updateGroup({
                    groupId: selectedWorkGroup.id,
                    body: formData,
                }).unwrap()
            } else {
                res = await createGroup(formData).unwrap()
            }

            if (res?.success) {
                toast.success(res?.message || 'Updated successfully!')
                reset()
                handelClose()
            } else {
                console.log(res, 'res')
                toast.error(res?.message || 'Something went wrong')
            }
        } catch (error) {
            toast.error('Something went wrong')
            console.error('rejected', error)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handelClose}
            title={`${selectedWorkGroup ? 'Edit' : 'Create'} Workgroup`}
            // isLoading={teamLoading}
        >
            <div className="modal__body p-0">
                <div className="custom-form">
                    <div className="custom-form-body">
                        <div className="row gutters-16">
                            <div className="col-12">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="WorkGroup Name"
                                    placeholder="Enter Name..."
                                    error={errors.name?.message}
                                    {...register('name')}
                                />
                            </div>
                            <div className="col-12">
                                <SelectBoxAsynk
                                    wrapperClassName="input--lg select-secondary select-left-spase-0"
                                    label="WorkGroup Team"
                                    fetchFunk={loadOptionsTeams}
                                    placeholder={'Select Team'}
                                    onChange={options => {
                                        setSelectedTeams(
                                            options.map(
                                                (option: {
                                                    value: number
                                                    label: string
                                                }) => ({
                                                    id: option.value,
                                                    name: option.label,
                                                }),
                                            ),
                                        )
                                    }}
                                    isMultiple={true}
                                    value={selectedTeams.map(team => ({
                                        value: team.id,
                                        label: team.name,
                                    }))}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBox
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Status"
                                            options={optionsTeamStatus}
                                            placeholder={'Select Status'}
                                            error={errors.status?.message}
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                            value={
                                                field.value ||
                                                optionsTeamStatus[0].value
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="country"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBox
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Country"
                                            options={countries}
                                            placeholder={'Select Country'}
                                            error={errors.country?.message}
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                            value={
                                                countries.find(
                                                    option =>
                                                        option.label ===
                                                            field.value ||
                                                        option.value ===
                                                            field.value,
                                                )?.value
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="state"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBox
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="State"
                                            options={states}
                                            placeholder="Select State"
                                            error={errors.state?.message}
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                            value={
                                                states.find(
                                                    option =>
                                                        option.label ===
                                                            field.value ||
                                                        option.value ===
                                                            field.value,
                                                )?.value
                                            }
                                            isDisabled={!states.length}
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-6">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="City"
                                    placeholder="Enter City..."
                                    error={errors.city?.message}
                                    {...register('city')}
                                />
                            </div>
                            <div className="col-6">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="Postal Code"
                                    placeholder="Enter Postal Code..."
                                    error={errors.postalCode?.message}
                                    {...register('postalCode')}
                                />
                            </div>
                            <div className="col-12">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="Street"
                                    placeholder="Enter Street..."
                                    error={errors.street?.message}
                                    {...register('street')}
                                />
                            </div>

                            <div className="col-12">
                                <hr className="form-separator" />
                            </div>
                            <div className="col-12">
                                <div className="search-widget overflow-visible">
                                    <div className="search-widget--head">
                                        <SearchInputWidthDrop
                                            placeholder="Add members by name or email..."
                                            label="Members"
                                            fetchFunk={getUsers}
                                            selectedItems={selectedUsers.map(
                                                user => user.id,
                                            )}
                                            selectedRoles={selectedUsers.map(
                                                user => user.role,
                                            )}
                                            onlyOneLead
                                            // fetchParams={`roles=${UserRoleEnum.TECHNICIANS}`}
                                            onSelected={selectUserSearch}
                                        />
                                    </div>
                                    <div className="search-widget--body">
                                        <SelectedUsersList
                                            list={selectedUsers}
                                            removeAction={removeSelectedUser}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="custom-form-footer justify-end">
                        <div className="flex-group flex-group--xs">
                            <Button
                                loading={isSubmitting}
                                type="submit"
                                onClick={handleSubmit(onSubmit)}
                                size="lg"
                                variant="primary-gradient"
                                // loading={createLoading || updateLoading}
                            >
                                <span>
                                    {selectedWorkGroup
                                        ? 'Save Changes'
                                        : 'Create Workgroup'}
                                </span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default AddWorkGroupModal
